/**
 * How good is the line RIGHT NOW, worked out from what the Voice SDK complains
 * about.
 *
 * The failure this exists to end: the WiFi drops in the middle of a call, and
 * the window carries on exactly as before. The timer keeps counting, the mute
 * button still works, the equalizer still bounces - and neither person can hear
 * the other. Twilio says so at the time (`reconnecting`, and a stream of
 * `warning` events off its stats monitor); nothing was listening.
 *
 * React-free and on its own, like `callError.ts` next door, because the answer
 * depends on state that is a poor fit for a boolean and the arithmetic is where
 * this goes wrong.
 *
 * ⚠ WHY A SET AND NOT A FLAG. `warning` and `warning-cleared` are raised PER
 * NAME and they overlap constantly: jitter rises, then MOS drops, then jitter
 * recovers. A boolean flipped false by the first `warning-cleared` would put the
 * strip out while the line is still bad - and it would look right in every
 * hand-test, because a single-cause problem clears cleanly. Hold the live names
 * and derive the grade from what is left.
 *
 * ⚠ WHY AN ALLOWLIST AND NOT AN IGNORE-LIST. The same `warning` event carries
 * the SDK's audio-level thresholds. An operator whose headset went quiet raises
 * `constant-audio-input-level`, which has nothing to do with the network, and
 * a strip that says "weak connection" at them would be a lie pointing at the
 * wrong thing to fix. Only the names below count, and an unknown name counts as
 * nothing - a new SDK version can add warnings without this file inventing a
 * fault out of one.
 */

/** The grade shown to the operator. Three steps, because two would lose the middle. */
export type CallLink = "ok" | "degraded" | "lost";

/**
 * The warnings that mean the NETWORK, taken from the SDK's stats monitor
 * thresholds (@twilio/voice-sdk, `statsMonitor.js`) plus the one that is raised
 * by hand.
 *
 * `low-bytes-received` is the one that matters most and the one a shorter list
 * always misses: it is what a one-way call looks like from here. The operator
 * is talking, their own bytes are going out fine, and nothing is coming back.
 *
 * `ice-connectivity-lost` is raised without any threshold payload and is
 * cleared from a different path inside the SDK, which is the second reason this
 * module reads NAMES only and never touches the data object - reading a
 * threshold off that one throws.
 */
export const NETWORK_WARNINGS: ReadonlySet<string> = new Set([
  "high-jitter",
  "high-rtt",
  "high-packet-loss",
  "high-packets-lost-fraction",
  "low-mos",
  "low-bytes-sent",
  "low-bytes-received",
  "ice-connectivity-lost",
]);

/** Is this `warning` about the line, rather than about somebody's microphone? */
export function isNetworkWarning(name: string): boolean {
  return NETWORK_WARNINGS.has(name);
}

/**
 * The grade, from the two things that are known separately.
 *
 * `reconnecting` outranks everything: media connectivity is gone and the SDK is
 * rebuilding it, so whatever the stats monitor last said about jitter is a
 * report on a line that no longer exists. Keeping the two inputs apart - a set
 * of names and a boolean - is what lets a late `warning-cleared` arrive during
 * a reconnect without writing "ok" over it.
 */
export function linkFrom(warnings: ReadonlySet<string>, reconnecting: boolean): CallLink {
  if (reconnecting) return "lost";
  return warnings.size > 0 ? "degraded" : "ok";
}
